import React from 'react';
import { ChildSummary, useChild } from '../contexts/ChildContext';

/**
 * Shown on parent pages that need a child context when multi-child.
 *
 * Behaviour:
 *   • If the parent has 1 child, ChildContext auto-selects — this modal
 *     never renders.
 *   • If the parent has 2+ and hasn't chosen yet, this modal blocks the
 *     underlying page until selection is made.
 *   • If 0 children, renders an "Önce öğrenci ekleyin" prompt.
 */

const F = { fontFamily: 'Inter' } as const;

interface Props {
  /** Prefix the "Çocuk Seçin" heading with the feature name ("Performans", "Ödevler", ...) */
  context?: string;
  /** Allow closing without selecting; defaults to false (forced selection). */
  dismissable?: boolean;
  onDismiss?: () => void;
}

const ChildSelectionModal: React.FC<Props> = ({ context, dismissable = false, onDismiss }) => {
  const { children: kids, selectedChild, loading, selectChild } = useChild();

  if (selectedChild) return null;           // already chosen
  if (loading) return null;                 // still fetching; parent context suppresses render
  if (kids.length <= 1) return null;        // auto-select handled by context

  const onPick = (c: ChildSummary) => selectChild(c.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={dismissable ? onDismiss : undefined} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[720px] p-8" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
        {dismissable && (
          <button onClick={onDismiss} className="absolute top-4 right-4 w-[40px] h-[40px] rounded-full hover:bg-gray-200 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
              <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          </button>
        )}
        <h2 style={{ ...F, fontSize: 24, fontWeight: 700, color: '#3973bf', marginBottom: 6 }}>
          {context ? `${context} — Çocuk Seçin` : 'Çocuk Seçin'}
        </h2>
        <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b', marginBottom: 22 }}>
          Görüntülemek istediğiniz öğrencinizi seçin.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kids.map(c => (
            <button key={c.id} onClick={() => onPick(c)}
              className="bg-[#fafafa] rounded-[16px] p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow text-left"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
              <div className="w-[56px] h-[56px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0">
                {/* AP62: çocuğun fotoğrafı seçim kartında da görünsün. Süresi
                    dolmuş imzalı URL'de kırık görsel yerine varsayılana düş. */}
                <img
                  src={c.avatarUrl || '/a-avatar.png'}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { if (e.currentTarget.src !== window.location.origin + '/a-avatar.png') e.currentTarget.src = '/a-avatar.png'; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }} className="truncate">
                  {c.name} {c.surname}
                </p>
                {c.className && (
                  <p style={{ ...F, fontSize: 13, color: '#3973bf', fontWeight: 600 }}>{c.className}</p>
                )}
                {c.institution?.name && (
                  <p style={{ ...F, fontSize: 12, color: '#888' }} className="truncate">{c.institution.name}</p>
                )}
              </div>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2">
                <path d="M7 4l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChildSelectionModal;
